/**
 * お問合せ自動化システム データモデル型定義。
 * 仕様書: docs/01_spec/spec_inquiry_automation.md C-5
 *
 * 既存 LINE Bot RAG 基盤 (qa_history) を Public Web チャット用に拡張する設計。
 * qa_history 自体は shared/src/types/line.ts で定義（後方互換のため optional 拡張）。
 */

import type { IsoOrTimestamp } from './common';
import type { QACitation } from './line';
import type {
  ChatMessageContentFormat,
  ChatSenderType,
  InquiryChannel,
  InquiryQueueResolution,
  InquiryQueueStatus,
  InquirySessionStatus,
} from '../constants';

// ─────────────────────────────────────────────
// qa_categories
// ─────────────────────────────────────────────
export interface QACategory {
  /** Firestore ドキュメント ID と一致。 */
  id: string;
  /** 表示名（日本語可）。 */
  name: string;
  /** 内部識別子（英数字、URL 用途）。 */
  slug: string;
  /** AI カテゴリ判定用ヒント文。 */
  description: string;
  /** 親カテゴリ ID（サブカテゴリ対応）。 */
  parentCategoryId: string | null;

  // ===== AI 設定（カテゴリ別オーバーライド） =====
  /** このカテゴリだけ AI 自動応答を OFF にできる。 */
  aiAutoEnabled: boolean;
  /** AI 信頼度の閾値（デフォルト 0.7、Phase 2 初期は 0.85 推奨）。 */
  confidenceThreshold: number;
  /** 強制エスカレーション語（「契約」「返金」など）。 */
  escalationKeywords: string[];

  // ===== 統計 =====
  /** このカテゴリに紐付く qa_history 件数。 */
  qaCount: number;
  /** 平均レビュー評価（1-5）。 */
  averageRating: number | null;

  // ===== メタ =====
  /** 'ai' = AI 自動生成、それ以外は admin user id。 */
  createdBy: 'ai' | string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
  archivedAt: IsoOrTimestamp | null;
}

// ─────────────────────────────────────────────
// inquiry_sessions（Public Web 用）
// ─────────────────────────────────────────────
export interface InquirySession {
  id: string;
  channel: InquiryChannel;
  /** 匿名認証 UID または cookie ベース識別子。 */
  userIdentifier: string | null;
  userDisplayName: string | null;
  status: InquirySessionStatus;
  startedAt: IsoOrTimestamp;
  lastMessageAt: IsoOrTimestamp;
  closedAt: IsoOrTimestamp | null;

  // 統計
  messageCount: number;
  aiAnswerCount: number;
  adminAnswerCount: number;

  /** セッション全体のユーザー満足度（個別 QA のレビューと別）。 */
  sessionRating: number | null;
}

// ─────────────────────────────────────────────
// chat_messages（Public Web 用）
// ─────────────────────────────────────────────
export interface ChatMessage {
  id: string;
  sessionId: string;

  // 送信者
  senderType: ChatSenderType;
  senderId: string | null;
  senderName: string | null;
  /** admin の場合は Google アカウントアバター URL。 */
  senderAvatar: string | null;

  // 内容
  content: string;
  contentFormat: ChatMessageContentFormat;

  // メタ
  /** 紐付く qa_history.id（AI 応答 / 事務応答時）。 */
  qaHistoryId: string | null;
  aiConfidence: number | null;
  citations: QACitation[] | null;

  // レビュー
  reviewCardShown: boolean;
  reviewSubmitted: boolean;
  /** 1-5 段階評価。 */
  reviewValue: number | null;
  /** 任意コメント（低評価時に表示）。 */
  reviewComment: string | null;

  /** 事務承認待ちの AI 下書きフラグ。true の間はユーザーに非表示。 */
  isEscalationDraft: boolean;

  createdAt: IsoOrTimestamp;
}

// ─────────────────────────────────────────────
// inquiry_queue（事務承認キュー）
// ─────────────────────────────────────────────
export interface InquiryQueueItem {
  id: string;
  sessionId: string;
  /** 元質問の chat_messages.id。 */
  messageId: string;
  userQuestion: string;
  aiDraftAnswer: string | null;
  aiConfidence: number | null;
  /** エスカレーション理由（信頼度不足 / キーワード一致 / ユーザー明示 等）。 */
  escalationReason: string;
  categoryId: string | null;
  status: InquiryQueueStatus;
  /** 任意の担当者割り当て。 */
  assignedTo: string | null;
  resolvedBy: string | null;
  resolution: InquiryQueueResolution | null;
  /** 事務が最終的に送信した回答文。 */
  finalAnswer: string | null;
  createdAt: IsoOrTimestamp;
  resolvedAt: IsoOrTimestamp | null;
}

// ─────────────────────────────────────────────
// settings/inquiry_automation
// ─────────────────────────────────────────────
export type EscalationNotifyChannel = 'email' | 'browser' | 'line_notify';

/**
 * データ保持期間設定（日数単位）+ 削除スケジュール。
 * - inquiry_sessions が削除されると chat_messages も連鎖削除（cleanup Function 内で対応）
 * - system_logs は別途 Firestore TTL ポリシーで管理
 * - GDPR 削除リクエストは設定不要（即時実行）
 */
export interface DataRetentionSettings {
  /** お問合せセッション保持日数。デフォルト 365 日。 */
  inquirySessionsDays: number;
  /** 解決済み事務承認キュー保持日数。デフォルト 180 日。 */
  inquiryQueueResolvedDays: number;
  /** Public Web の QA 履歴保持日数（学習データ）。デフォルト 1825 日 (5 年)。 */
  qaHistoryPublicDays: number;
  /** エスカレ下書き (isEscalationDraft=true) 保持日数。デフォルト 30 日。 */
  escalationDraftsDays: number;
  /** 未承認 QA (adminReviewed=false) 保持日数。デフォルト 90 日。 */
  unreviewedQaDays: number;

  /** 削除スケジュール頻度。 */
  cleanupFrequency: 'weekly' | 'monthly';
  /** 週次の場合の曜日 (0=日曜 〜 6=土曜)。 */
  cleanupDayOfWeek: number;
  /** 月次の場合の日 (1-28)。 */
  cleanupDayOfMonth: number;
  /** 実行時刻 (JST, "HH:MM" 形式)。デフォルト "03:00"。 */
  cleanupTimeJst: string;
  /** 最終実行日時。 */
  lastCleanupAt: IsoOrTimestamp | null;
  /** 最終実行結果。 */
  lastCleanupResult: {
    totalDeleted: number;
    inquirySessions: number;
    chatMessages: number;
    inquiryQueue: number;
    qaHistory: number;
    escalationDrafts: number;
    unreviewedQa: number;
    errorMessage?: string;
  } | null;
}

export interface InquiryAutomationSettings {
  /** 全体スイッチ。false なら全質問が事務承認パスへ。 */
  aiAutoEnabled: boolean;
  /** カテゴリ未設定時の信頼度閾値。 */
  defaultConfidenceThreshold: number;
  /** AI 自動応答 OFF 時でも下書きを生成するか（事務作業効率化）。 */
  generateDraftWhenOff: boolean;

  // 通知
  escalationNotifyChannels: EscalationNotifyChannel[];
  /** 通知先 admin user id 配列。 */
  escalationNotifyRecipients: string[];

  // レビューカード
  reviewCardEnabled: boolean;
  /** 回答後何秒でレビューカードを表示するか。 */
  reviewCardDelaySeconds: number;

  // PII
  piiMaskingEnabled: boolean;

  // データ保持（Phase 6 で追加）
  dataRetention?: DataRetentionSettings;

  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

export const DEFAULT_DATA_RETENTION: DataRetentionSettings = {
  inquirySessionsDays: 365,
  inquiryQueueResolvedDays: 180,
  qaHistoryPublicDays: 1825,
  escalationDraftsDays: 30,
  unreviewedQaDays: 90,
  cleanupFrequency: 'monthly',
  cleanupDayOfWeek: 0,
  cleanupDayOfMonth: 1,
  cleanupTimeJst: '03:00',
  lastCleanupAt: null,
  lastCleanupResult: null,
};

/**
 * settings コレクションのドキュメント ID。
 * 既存の settings/general や settings/secrets_status と並列。
 */
export const INQUIRY_AUTOMATION_SETTINGS_DOC_ID = 'inquiry_automation';

/**
 * 新規 Firestore コレクション名（spec_inquiry_automation.md C-5）。
 */
export const INQUIRY_COLLECTIONS = {
  qaCategories: 'qa_categories',
  inquirySessions: 'inquiry_sessions',
  chatMessages: 'chat_messages',
  inquiryQueue: 'inquiry_queue',
} as const;
